import {
  applyServerValidationErrors,
  initializeFormValidation,
  type ServerValidationErrors,
  validateForm,
} from "./form-validation";

export type FormAjaxResponse = {
  success?: boolean;
  message?: string;
  errors?: ServerValidationErrors;
  [key: string]: unknown;
};

export type FormProtection = {
  prepare(form: HTMLFormElement): Promise<void>;
  reset?(form: HTMLFormElement): void;
};

export type FormSuccessHandler = (context: {
  form: HTMLFormElement;
  response: FormAjaxResponse;
}) => void | Promise<void>;

const protections = new Map<string, FormProtection>();
const successHandlers = new Map<string, FormSuccessHandler>();

/** Register an optional submit protection, such as a CAPTCHA provider. */
export function registerFormProtection(name: string, protection: FormProtection): void {
  protections.set(name, protection);
}

/** Register a form-specific success action without coupling it to transport. */
export function registerFormSuccessHandler(name: string, handler: FormSuccessHandler): void {
  successHandlers.set(name, handler);
}

function setFormError(form: HTMLFormElement, message: string): void {
  const target = form.querySelector<HTMLElement>("[data-form-error]");
  if (!target) {
    return;
  }

  target.setAttribute("aria-live", "polite");
  target.replaceChildren();
  const error = document.createElement("p");
  error.className = target.dataset.formErrorClass ?? "error";
  error.textContent = message;
  target.append(error);
}

function clearFormError(form: HTMLFormElement): void {
  form.querySelector<HTMLElement>("[data-form-error]")?.replaceChildren();
}

function formDataAsUrlSearchParams(form: HTMLFormElement): URLSearchParams {
  const data = new URLSearchParams();
  new FormData(form).forEach((value, key) => {
    if (typeof value === "string") {
      data.append(key, value);
    }
  });
  return data;
}

function nextPaint(): Promise<void> {
  // The first frame gives Alpine a chance to apply x-show. The browser paints
  // between the two callbacks, so the spinner is actually visible before a
  // native form navigation begins.
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : "Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.";
}

/**
 * Generic Alpine form controller. The default submits natively; opt into an
 * AJAX response with `data-form-submit="ajax"`. Optional submit protections
 * and success handlers are selected declaratively by name on the form.
 */
export function formDataContext() {
  return {
    form: null as HTMLFormElement | null,
    isSubmitting: false,

    init() {
      this.form = this.$root as HTMLFormElement;
      initializeFormValidation(this.form);
      if (!this.form.dataset.validationManualSubmit) {
        this.form.addEventListener("submit", (event) => {
          event.preventDefault();
          void this.submit();
        });
      }
    },

    async submit() {
      if (!this.form || this.isSubmitting || !validateForm(this.form)) {
        return;
      }

      const protection = protections.get(this.form.dataset.formProtection ?? "");
      if (this.form.dataset.formProtection && !protection) {
        setFormError(this.form, "Der Formularschutz konnte nicht vorbereitet werden. Bitte lade die Seite neu.");
        return;
      }

      clearFormError(this.form);
      this.setSubmitting(true);
      try {
        await protection?.prepare(this.form);

        if (this.form.dataset.formSubmit !== "ajax") {
          await nextPaint();
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }

        const response = await fetch(this.form.action, {
          method: this.form.method || "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
          body: formDataAsUrlSearchParams(this.form),
        });
        if (!response.ok) {
          throw new Error("Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.");
        }

        const data = await response.json() as FormAjaxResponse;
        if (data.success) {
          const handler = successHandlers.get(this.form.dataset.formSuccessHandler ?? "");
          if (handler) {
            await handler({ form: this.form, response: data });
          } else {
            this.setSubmitting(false);
          }
          this.form.dispatchEvent(new CustomEvent("ymh:form-success", { bubbles: true, detail: data }));
          return;
        }

        const fieldErrorsApplied = applyServerValidationErrors(this.form, data.errors);
        if (data.message && (!fieldErrorsApplied || this.form.dataset.formShowResponseMessage === "true")) {
          setFormError(this.form, data.message);
        }
        protection?.reset?.(this.form);
        this.setSubmitting(false);
      } catch (error) {
        protection?.reset?.(this.form);
        setFormError(this.form, errorMessage(error));
        this.setSubmitting(false);
      }
    },

    setSubmitting(active: boolean) {
      this.isSubmitting = active;
      if (!this.form) {
        return;
      }

      this.form.toggleAttribute("aria-busy", active);
      (Array.from(this.form.querySelectorAll('button[type="submit"]')) as HTMLButtonElement[]).forEach((button) => {
        button.disabled = active;
      });
    },
  };
}
